import { Modal, PixelIcon } from '@/components/ui';
import { useShop } from '@/contexts/ShopContext';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { getMenuHapticPattern } from '@/utils/haptics';
import { FONT, FONT_SIZE, SPACING, RADIUS, COLOR, modalButtonStyle, statCardStyle, infoRowStyle } from '@/config/designTokens';
import { motion } from 'motion/react';
import React, { useEffect, useState } from 'react';

interface NoLivesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShop: () => void;
}

const NoLivesModal: React.FC<NoLivesModalProps> = ({
  isOpen,
  onClose,
  onShop,
}) => {
  const { shopState, getFormattedTimeUntilNextLife, shopManager } = useShop();
  const { pattern } = useHapticFeedback();
  const [timeUntilRefill, setTimeUntilRefill] = useState<string>('');
  const maxLives = shopManager?.getMaxLives?.() ?? 5;
  const [hasTriggeredOpenFeedback, setHasTriggeredOpenFeedback] = useState(false);

  // Update time until next life refill
  useEffect(() => {
    if (isOpen && shopState.lives < maxLives) {
      const updateTimer = () => {
        setTimeUntilRefill(getFormattedTimeUntilNextLife());
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [isOpen, shopState.lives, maxLives, getFormattedTimeUntilNextLife]);

  useEffect(() => {
    if (!isOpen) {
      setHasTriggeredOpenFeedback(false);
      return;
    }
    if (hasTriggeredOpenFeedback) return;
    pattern(getMenuHapticPattern('warning'));
    setHasTriggeredOpenFeedback(true);
  }, [hasTriggeredOpenFeedback, isOpen, pattern]);

  const renderHeader = () => (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        textAlign: 'center',
        marginBottom: SPACING.lg,
      }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
        style={{ marginBottom: SPACING.sm }}
      >
        <PixelIcon name="warning" size={48} color={COLOR.warningLight} />
      </motion.div>
      <motion.h1
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        style={{
          fontSize: FONT_SIZE.modalTitle,
          fontWeight: 'bold',
          color: COLOR.warningLight,
          margin: `0 0 ${SPACING.xs} 0`,
          fontFamily: FONT.body,
          textShadow: '2px 2px 0 #dd6b20',
        }}
      >
        No Lives Left!
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        style={{
          fontSize: FONT_SIZE.body,
          color: COLOR.textSecondary,
          margin: 0,
          fontFamily: FONT.body,
        }}
      >
        You need lives to continue playing
      </motion.p>
    </motion.div>
  );

  const renderLivesDisplay = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.6, duration: 0.5 }}
      style={{
        ...statCardStyle(COLOR.warningLight),
        textAlign: 'center',
        marginBottom: SPACING.lg,
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: SPACING.sm,
        marginBottom: SPACING.sm,
      }}>
        {Array.from({ length: maxLives }, (_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              delay: 0.8 + i * 0.1,
              type: "spring",
              stiffness: 300,
              damping: 20
            }}
            style={{
              filter: i < shopState.lives ? 'none' : 'grayscale(100%)',
              opacity: i < shopState.lives ? 1 : 0.4,
            }}
          >
            <PixelIcon
              name="life"
              size={32}
              color={i < shopState.lives ? COLOR.life : '#4a5568'}
            />
          </motion.div>
        ))}
      </div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        style={{
          fontSize: FONT_SIZE.buttonNormal,
          fontWeight: 'bold',
          color: COLOR.warningLight,
          margin: 0,
          fontFamily: FONT.body,
        }}
      >
        {shopState.lives} / {maxLives} Lives
      </motion.p>
    </motion.div>
  );

  const renderOptions = () => (
    <div style={{
      display: 'grid',
      gap: SPACING.md,
      marginBottom: SPACING.lg,
    }}>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.4 }}
        style={infoRowStyle(COLOR.statBlue)}
      >
        <div style={{ flexShrink: 0 }}>
          <PixelIcon name="clock" size={28} color={COLOR.statBlue} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 style={{
            fontSize: FONT_SIZE.buttonNormal,
            fontWeight: 'bold',
            color: COLOR.statBlue,
            margin: `0 0 2px 0`,
            fontFamily: FONT.body,
          }}>
            Wait for Refill
          </h4>
          <p style={{
            fontSize: FONT_SIZE.caption,
            color: COLOR.textSecondary,
            margin: 0,
            fontFamily: FONT.body,
          }}>
            1 free life every 30 min
            {shopState.lives < maxLives && timeUntilRefill && (
              <span style={{ color: COLOR.statBlue, fontWeight: 'bold', marginLeft: '6px' }}>
                Next: {timeUntilRefill}
              </span>
            )}
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.5 }}
        style={infoRowStyle(COLOR.statYellow)}
      >
        <div style={{ flexShrink: 0 }}>
          <PixelIcon name="shop" size={28} color={COLOR.statYellow} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 style={{
            fontSize: FONT_SIZE.buttonNormal,
            fontWeight: 'bold',
            color: COLOR.statYellow,
            margin: `0 0 2px 0`,
            fontFamily: FONT.body,
          }}>
            Buy Lives
          </h4>
          <p style={{
            fontSize: FONT_SIZE.caption,
            color: COLOR.textSecondary,
            margin: 0,
            fontFamily: FONT.body,
          }}>
            3 lives for 50 coins
            <span style={{ color: COLOR.statYellow, fontWeight: 'bold', marginLeft: '6px' }}>
              ({shopState.coins} coins)
            </span>
          </p>
        </div>
      </motion.div>
    </div>
  );

  const renderActions = () => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: SPACING.md,
    }}>
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        onClick={onShop}
        style={modalButtonStyle(COLOR.buttonAmber)}
        whileHover={{ backgroundColor: COLOR.buttonAmberHover, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <PixelIcon name="shop" size={20} color="#ffffff" />
        <span>Visit Shop</span>
      </motion.button>

      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.7 }}
        onClick={onClose}
        style={modalButtonStyle(COLOR.buttonGray)}
        whileHover={{ backgroundColor: COLOR.buttonGrayHover, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <PixelIcon name="home" size={18} color="#ffffff" />
        <span>Go Home</span>
      </motion.button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="warning"
      size="medium"
      closeOnBackdrop={false}
      closeOnEscape={false}
      showCloseButton={false}
    >
      <div className="menu-delight-panel menu-delight-panel--warning">
        <div className="menu-delight-panel__splashes" aria-hidden="true">
          <span className="menu-delight-panel__splash menu-delight-panel__splash--1" />
          <span className="menu-delight-panel__splash menu-delight-panel__splash--2" />
          <span className="menu-delight-panel__splash menu-delight-panel__splash--4" />
        </div>
        {renderHeader()}
        {renderLivesDisplay()}
        {renderOptions()}
        {renderActions()}
      </div>
    </Modal>
  );
};

export default NoLivesModal;
